import React, { Component } from 'react';
import moment from 'moment-timezone';
import { get, isEmpty } from 'lodash';
import { rateTypeNameFinder } from 'helpers/rateTypeName';
import { EMPTY_DATE, formatCurrency } from 'helpers/dashboard';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { formatTimeTz, formatTableDateTz } from 'helpers/datetime';
import { isCancellationFee, isCancellationPayment } from 'helpers/cancellations';
import { SHOW_TOOLTIP_COLUMNS, createOverflowChecker } from 'helpers/dashboard';
import { UncontrolledTooltip } from 'reactstrap';
import classNames from 'classnames';
import { hasPendingFailedPayment } from '@helpers/payment';

export default class FinancesJobItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOverflowing: {},
    };
    this.columnRefs = {};
    SHOW_TOOLTIP_COLUMNS.financesJobs.forEach(column => {
      this.columnRefs[column] = React.createRef();
    });
  }

  componentDidMount() {
    const checkOverflow = createOverflowChecker('financesJobs', this.columnRefs, this.setState.bind(this));

    this.resizeObserver = new ResizeObserver(checkOverflow);
    SHOW_TOOLTIP_COLUMNS.financesJobs.forEach(column => {
      this.resizeObserver.observe(this.columnRefs[column].current);
    });
  }

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  computeTotalPayment = () => {
    const { job } = this.props;
    if (job.go4PlatformPaid) return 0;

    const jobPayment = Number(get(job, 'payment.eoTotalCharge', 0));
    const jobRefundAmount = Number(get(job, 'payment.refundAmount', 0));
    const jobModificationPayment = Number(get(job, 'jobModification.payment.eoTotalCharge', 0));
    const jobModificationRefundAmount = Number(get(job, 'jobModification.payment.refundAmount', 0));
    return (
      Math.max(0, jobPayment - jobRefundAmount) +
      Math.max(0, jobModificationPayment - jobModificationRefundAmount)
    );
  };

  hasJobRefund = job => {
    const jobRefund = Number(get(job, 'payment.refundAmount', 0));
    const jobModificationRefund = Number(get(job, 'jobModification.payment.refundAmount', 0));
    return !!(jobRefund || jobModificationRefund);
  };

  formatUserName = () => {
    const { job } = this.props;
    const { user, cancelledAt } = job;
    const userName = get(user, 'name', 'USER DELETED');
    const hasRefund = this.hasJobRefund(job);
    const hasValidCancellation = moment(cancelledAt).isValid();

    if (!hasValidCancellation) return hasRefund ? `${userName} - Refund` : userName;

    if (isCancellationPayment(job))
      return hasRefund ? `${userName} - Cancellation Payment Refund` : `${userName} - Cancellation Payment`;

    if (isCancellationFee(job))
      return hasRefund ? `${userName} - Cancellation Fee Refund` : `${userName} - Cancellation Fee`;
  };

  datePaidColumnValue = () => {
    const { job } = this.props;
    const { shiftLocation } = job;
    const tz = zipcode_to_timezone.lookup(shiftLocation.address.zipCode) || moment.tz.guess();
    const paidAt = get(job, 'payment.paidAt', null);

    if (hasPendingFailedPayment(job)) return 'Failed Payment';

    return paidAt ? formatTableDateTz(paidAt, tz) : EMPTY_DATE;
  };

  render() {
    const { job } = this.props;
    const { shiftLocation } = job;
    const jobModification = get(job, 'jobModification', {});
    const tz = zipcode_to_timezone.lookup(shiftLocation.address.zipCode) || moment.tz.guess();

    const isFixedRateModification =
      !isEmpty(jobModification) && (jobModification.rateTypeId ?? job.rateTypeId) === 1;
    const startTime = jobModification.startTime || job.startTime;
    const endTime = jobModification.endTime || job.endTime;
    const payRate = jobModification.payRate || job.payRate;
    const rateTypeId = jobModification.rateTypeId ?? job.rateTypeId;

    return (
      <div className="finances-job-item">
        <div className="col-md-2" id={`atName-${job.id}`} ref={this.columnRefs['atName']}>
          {this.formatUserName()}
          {this.state.isOverflowing.atName && (
            <UncontrolledTooltip target={`atName-${job.id}`}>{this.formatUserName()}</UncontrolledTooltip>
          )}
        </div>
        <div className="col-md-1" id={`jobDate-${job.id}`} ref={this.columnRefs['jobDate']}>
          {formatTableDateTz(startTime, tz)}
          {this.state.isOverflowing.jobDate && (
            <UncontrolledTooltip target={`jobDate-${job.id}`}>
              {formatTableDateTz(startTime, tz)}
            </UncontrolledTooltip>
          )}
        </div>
        <div className="col-md-2" id={`jobTime-${job.id}`} ref={this.columnRefs['jobTime']}>
          {isFixedRateModification ? '-' : `${formatTimeTz(startTime, tz)} - ${formatTimeTz(endTime, tz)}`}
          {this.state.isOverflowing.jobTime && (
            <UncontrolledTooltip target={`jobTime-${job.id}`}>
              {isFixedRateModification ? '-' : `${formatTimeTz(startTime, tz)} - ${formatTimeTz(endTime, tz)}`}
            </UncontrolledTooltip>
          )}
        </div>
        <div className="col-md-3" id={`location-${job.id}`} ref={this.columnRefs['location']}>
          {`${shiftLocation.address.city}, ${shiftLocation.address.state}`}
          {this.state.isOverflowing.location && (
            <UncontrolledTooltip target={`location-${job.id}`}>
              {`${shiftLocation.address.city}, ${shiftLocation.address.state}`}
            </UncontrolledTooltip>
          )}
        </div>
        <div className="col-md-1" id={`rate-${job.id}`} ref={this.columnRefs['rate']}>
          {`${formatCurrency({
            currency: payRate,
            dollarSign: true,
          })}
          /
          ${rateTypeNameFinder(this.props.rateTypes, rateTypeId)}`}{' '}
          {this.state.isOverflowing.rate && (
            <UncontrolledTooltip target={`rate-${job.id}`}>
              {`${formatCurrency({
                currency: payRate,
                dollarSign: true,
              })}
          /
          ${rateTypeNameFinder(this.props.rateTypes, rateTypeId)}`}
            </UncontrolledTooltip>
          )}
        </div>
        <div className="col-md-2" id={`totalPayment-${job.id}`} ref={this.columnRefs['totalPayment']}>
          {formatCurrency({
            currency: this.computeTotalPayment(),
            dollarSign: true,
          })}{' '}
          {this.state.isOverflowing.totalPayment && (
            <UncontrolledTooltip target={`totalPayment-${job.id}`}>
              {formatCurrency({
                currency: this.computeTotalPayment(),
                dollarSign: true,
              })}
            </UncontrolledTooltip>
          )}
        </div>
        <div
          className={classNames('col-md-1', {
            'red-text': hasPendingFailedPayment(job),
          })}
          id={`datePaid-${job.id}`}
          ref={this.columnRefs['datePaid']}
        >
          {this.datePaidColumnValue()}
          {this.state.isOverflowing.datePaid && (
            <UncontrolledTooltip target={`datePaid-${job.id}`}>
              {this.datePaidColumnValue()}
            </UncontrolledTooltip>
          )}
        </div>
      </div>
    );
  }
}
